'use client'

import React from 'react'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { 
  Leaf, 
  FlaskConical, 
  Sparkles, 
  Heart, 
  Trees, 
  Cannabis,
  Thermometer,
  Beaker,
  BookOpen,
  AlertCircle
} from 'lucide-react'
import type { TerpeneRow } from '@/types/supabase'

interface TerpeneDetailDisplayProps {
  selectedTerpene: TerpeneRow | null
}

export default function TerpeneDetailDisplay({ selectedTerpene }: TerpeneDetailDisplayProps) {
  if (!selectedTerpene) {
    return (
      <div className="h-full min-h-[400px] bg-black/20 backdrop-blur-sm rounded-lg border border-white/10 p-6 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-white/30 mx-auto mb-3" />
          <p className="text-white/60">Select a terpene to view details</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full bg-black/20 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden">
      {/* Header */}
      <div className="p-4 sm:p-6 bg-gradient-to-r from-emerald-500/10 to-green-500/10 border-b border-white/10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Leaf className="h-5 w-5 text-emerald-400" />
              <h2 className="text-xl sm:text-2xl font-bold text-white">
                {selectedTerpene.name}
              </h2>
            </div>
            {selectedTerpene.alternate_names && selectedTerpene.alternate_names.length > 0 && (
              <p className="text-sm text-white/60">
                Also known as: {selectedTerpene.alternate_names.join(', ')}
              </p>
            )}
          </div>
          {selectedTerpene.category && (
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
              {selectedTerpene.category}
            </Badge>
          )}
        </div>
      </div>

      {/* Content - Scrollable */}
      <ScrollArea className="h-[calc(100%-100px)] sm:h-[calc(100%-120px)]">
        <div className="p-4 sm:p-6 space-y-6">
          {/* Description */}
          {selectedTerpene.description && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-white/80">
                <BookOpen className="h-4 w-4" />
                Overview
              </div>
              <p className="text-sm text-white/70 leading-relaxed">
                {selectedTerpene.description}
              </p>
            </div>
          )}

          {/* Primary Effects */}
          {selectedTerpene.primary_effects && selectedTerpene.primary_effects.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-white/80">
                <Sparkles className="h-4 w-4" />
                Primary Effects
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedTerpene.primary_effects.map((effect, index) => (
                  <Badge 
                    key={index}
                    variant="secondary"
                    className="bg-blue-500/10 text-blue-300 border-blue-500/20"
                  >
                    {effect}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Medical Benefits */}
          {selectedTerpene.medical_benefits && selectedTerpene.medical_benefits.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-white/80">
                <Heart className="h-4 w-4" />
                Medical Benefits
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedTerpene.medical_benefits.map((benefit, index) => (
                  <Badge 
                    key={index}
                    variant="secondary"
                    className="bg-rose-500/10 text-rose-300 border-rose-500/20"
                  >
                    {benefit}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <Separator className="bg-white/10" />

          {/* Found in */}
          {selectedTerpene.natural_sources && selectedTerpene.natural_sources.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-white/80">
                <Trees className="h-4 w-4" />
                Found in
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedTerpene.natural_sources.map((source, index) => (
                  <Badge 
                    key={index}
                    variant="outline"
                    className="bg-green-500/5 text-green-300 border-green-500/20"
                  >
                    {source}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Cannabis Strains */}
          {selectedTerpene.cannabis_strains && selectedTerpene.cannabis_strains.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-white/80">
                <Cannabis className="h-4 w-4" />
                Common Strains
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedTerpene.cannabis_strains.map((strain, index) => (
                  <Badge 
                    key={index}
                    variant="outline"
                    className="bg-purple-500/5 text-purple-300 border-purple-500/20"
                  >
                    {strain}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <Separator className="bg-white/10" />

          {/* Chemical Properties */}
          <div className="grid grid-cols-2 gap-4">
            {selectedTerpene.boiling_point_c && (
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs font-semibold text-white/60">
                  <Thermometer className="h-3 w-3" />
                  Boiling Point
                </div>
                <p className="text-sm text-white/80">
                  {selectedTerpene.boiling_point_c}°C / {selectedTerpene.boiling_point_f}°F
                </p>
              </div>
            )}
            
            {selectedTerpene.molecular_formula && (
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs font-semibold text-white/60">
                  <Beaker className="h-3 w-3" />
                  Formula
                </div>
                <p className="text-sm text-white/80 font-mono">
                  {selectedTerpene.molecular_formula}
                </p>
              </div>
            )}
            
            {selectedTerpene.molecular_weight && (
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs font-semibold text-white/60">
                  <FlaskConical className="h-3 w-3" />
                  Molecular Weight
                </div>
                <p className="text-sm text-white/80">
                  {selectedTerpene.molecular_weight} g/mol
                </p>
              </div>
            )}
            
            {selectedTerpene.concentration_range && (
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs font-semibold text-white/60">
                  <FlaskConical className="h-3 w-3" />
                  Concentration
                </div>
                <p className="text-sm text-white/80">
                  {selectedTerpene.concentration_range}
                </p>
              </div>
            )}
          </div>

          {/* Aroma & Flavor */}
          {(selectedTerpene.aroma_profile || selectedTerpene.flavor_notes) && (
            <>
              <Separator className="bg-white/10" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {selectedTerpene.aroma_profile && selectedTerpene.aroma_profile.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-white/60">Aroma Profile</div>
                    <div className="flex flex-wrap gap-1">
                      {selectedTerpene.aroma_profile.map((aroma, index) => (
                        <Badge 
                          key={index}
                          variant="outline"
                          className="text-xs bg-yellow-500/5 text-yellow-300 border-yellow-500/20"
                        >
                          {aroma}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedTerpene.flavor_notes && selectedTerpene.flavor_notes.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-white/60">Flavor Notes</div>
                    <div className="flex flex-wrap gap-1">
                      {selectedTerpene.flavor_notes.map((flavor, index) => (
                        <Badge 
                          key={index}
                          variant="outline"
                          className="text-xs bg-orange-500/5 text-orange-300 border-orange-500/20"
                        >
                          {flavor}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Research Notes */}
          {selectedTerpene.research_notes && (
            <>
              <Separator className="bg-white/10" />
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-white/80">
                  <BookOpen className="h-4 w-4" />
                  Research Notes
                </div>
                <p className="text-xs text-white/60 leading-relaxed">
                  {selectedTerpene.research_notes}
                </p>
              </div>
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}